/**
 * Copyright (c) 2013-present, Facebook, Inc.
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree. An additional grant
 * of patent rights can be found in the PATENTS file in the same directory.
 *
 * @providesModule setRelayModernMutationConfigs
 * 
 * @format
 */

'use strict';

function setRelayModernMutationConfigs(configs, operation, optimisticUpdater, updater) {
  var configOptimisticUpdates = optimisticUpdater ? [optimisticUpdater] : [];
  var configUpdates = updater ? [updater] : [];
  configs.forEach(function (config) {
    switch (config.type) {
      case 'NODE_DELETE':
        var nodeDeleteResult = nodeDelete(config, operation);
        if (nodeDeleteResult) {
          configOptimisticUpdates.push(nodeDeleteResult);
          configUpdates.push(nodeDeleteResult);
        }
        break;
      case 'RANGE_ADD':
        var rangeAddResult = rangeAdd(config, operation);
        if (rangeAddResult) {
          configOptimisticUpdates.push(rangeAddResult);
          configUpdates.push(rangeAddResult);
        }
        break;
      case 'RANGE_DELETE':
        var rangeDeleteResult = rangeDelete(config, operation);
        if (rangeDeleteResult) {
          configOptimisticUpdates.push(rangeDeleteResult);
          configUpdates.push(rangeDeleteResult);
        }
        break;
    }
  });
  optimisticUpdater = function optimisticUpdater(store, data) {
    configOptimisticUpdates.forEach(function (eachOptimisticUpdater) {
      eachOptimisticUpdater(store, data);
    });
  };
  updater = function updater(store, data) {
    configUpdates.forEach(function (eachUpdater) {
      eachUpdater(store, data);
    });
  };
  return { optimisticUpdater: optimisticUpdater, updater: updater };
}

function nodeDelete(config, operation) {
  var updater = void 0;
  if (config.type !== 'NODE_DELETE') {
    return;
  }
  var deletedIDFieldName = config.deletedIDFieldName;

  var rootField = getRootField(operation);
  if (rootField) {
    updater = function updater(store, data) {
      var payload = store.getRootField(rootField);
      if (!payload) {
        return;
      }
      var deleteID = payload.getValue(deletedIDFieldName);
      var deleteIDs = Array.isArray(deleteID) ? deleteID : [deleteID];
      deleteIDs.forEach(function (id) {
        if (id && typeof id === 'string') {
          store['delete'](id);
        }
      });
    };
  }
  return updater;
}

function rangeAdd(config, operation) {
  var updater = void 0;
  if (config.type !== 'RANGE_ADD') {
    return;
  }
  var parentID = config.parentID,
      connectionInfo = config.connectionInfo,
      edgeName = config.edgeName;

  if (!parentID) {
    require('fbjs/lib/warning')(false, 'setRelayModernMutationConfigs: For mutation config RANGE_ADD ' + 'to work you must include a parentID');
    return;
  }
  var rootField = getRootField(operation);
  if (connectionInfo && rootField) {
    updater = function updater(store, data) {
      var parent = store.get(parentID);
      if (parent) {
        var payload = store.getRootField(rootField);
        if (!payload) {
          return;
        }
        var serverEdge = payload.getLinkedRecord(edgeName);
        var _iteratorNormalCompletion = true;
        var _didIteratorError = false;
        var _iteratorError = undefined;

        try {
          for (var _iterator = connectionInfo[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
            var info = _step.value;

            if (serverEdge) {
              var connection = require('./RelayConnectionHandler').getConnection(parent, info.key, info.filters);
              if (!connection) {
                return;
              }
              var clientEdge = require('./RelayConnectionHandler').buildConnectionEdge(store, connection, serverEdge);
              if (!clientEdge) {
                return;
              }
              switch (info.rangeBehavior) {
                case 'append':
                  require('./RelayConnectionHandler').insertEdgeAfter(connection, clientEdge);
                  break;
                case 'ignore':
                  // Do nothing
                  break;
                case 'prepend':
                  require('./RelayConnectionHandler').insertEdgeBefore(connection, clientEdge);
                  break;
                default:
                  require('fbjs/lib/warning')(false, 'setRelayModernMutationConfigs: RANGE_ADD range behavior ' + ('\'' + info.rangeBehavior + '\' will not work as expected in RelayModern, ') + "supported range behaviors are 'append', 'prepend', and " + "'ignore'");
                  break;
              }
            }
          }
        } catch (err) {
          _didIteratorError = true;
          _iteratorError = err;
        } finally {
          try {
            if (!_iteratorNormalCompletion && _iterator['return']) {
              _iterator['return']();
            }
          } finally {
            if (_didIteratorError) {
              throw _iteratorError;
            }
          }
        }
      }
    };
  }
  return updater;
}

function rangeDelete(config, operation) {
  var updater = void 0;
  if (config.type !== 'RANGE_DELETE') {
    return;
  }
  var parentID = config.parentID,
      connectionKeys = config.connectionKeys,
      pathToConnection = config.pathToConnection,
      deletedIDFieldName = config.deletedIDFieldName;

  if (!parentID) {
    require('fbjs/lib/warning')(false, 'setRelayModernMutationConfigs: For mutation config RANGE_DELETE ' + 'to work you must include a parentID');
    return;
  }
  var rootField = getRootField(operation);
  if (rootField) {
    updater = function updater(store, data) {
      if (data) {
        var deleteIDs = [];
        var deletedIDField = data[rootField];
        if (deletedIDField && Array.isArray(deletedIDFieldName)) {
          var _iteratorNormalCompletion2 = true;
          var _didIteratorError2 = false;
          var _iteratorError2 = undefined;

          try {
            for (var _iterator2 = deletedIDFieldName[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
              var eachField = _step2.value;

              if (deletedIDField && typeof deletedIDField === 'object') {
                deletedIDField = deletedIDField[eachField];
              }
            }
          } catch (err) {
            _didIteratorError2 = true;
            _iteratorError2 = err;
          } finally {
            try {
              if (!_iteratorNormalCompletion2 && _iterator2['return']) {
                _iterator2['return']();
              }
            } finally {
              if (_didIteratorError2) {
                throw _iteratorError2;
              }
            }
          }

          if (Array.isArray(deletedIDField)) {
            deletedIDField.forEach(function (idObject) {
              if (idObject && idObject.id && typeof idObject === 'object' && typeof idObject.id === 'string') {
                deleteIDs.push(idObject.id);
              }
            });
          } else if (deletedIDField && deletedIDField.id && typeof deletedIDField.id === 'string') {
            deleteIDs.push(deletedIDField.id);
          }
        } else if (deletedIDField && typeof deletedIDFieldName === 'string' && typeof deletedIDField === 'object') {
          deletedIDField = deletedIDField[deletedIDFieldName];
          if (typeof deletedIDField === 'string') {
            deleteIDs.push(deletedIDField);
          } else if (Array.isArray(deletedIDField)) {
            deletedIDField.forEach(function (id) {
              if (typeof id === 'string') {
                deleteIDs.push(id);
              }
            });
          }
        }
        deleteNode(parentID, connectionKeys, pathToConnection, store, deleteIDs);
      }
    };
  }
  return updater;
}

function deleteNode(parentID, connectionKeys, pathToConnection, store, deleteIDs) {
  require('fbjs/lib/warning')(connectionKeys, 'setRelayModernMutationConfigs: RANGE_DELETE must provide a ' + 'connectionKeys');
  var parent = store.get(parentID);
  if (!parent) {
    return;
  }
  if (pathToConnection.length >= 2) {
    var recordProxy = parent;
    for (var i = 1; i < pathToConnection.length - 1; i++) {
      if (recordProxy) {
        recordProxy = recordProxy.getLinkedRecord(pathToConnection[i]);
      }
    }
    // Should never enter loop except edge cases
    if (connectionKeys && recordProxy) {
      var _iteratorNormalCompletion3 = true;
      var _didIteratorError3 = false;
      var _iteratorError3 = undefined;

      try {
        var _loop = function _loop() {
          var key = _step3.value;

          var connection = require('./RelayConnectionHandler').getConnection(recordProxy, key.key, key.filters);
          if (connection) {
            deleteIDs.forEach(function (deleteID) {
              require('./RelayConnectionHandler').deleteNode(connection, deleteID);
            });
            return 'break';
          }
        };

        for (var _iterator3 = connectionKeys[Symbol.iterator](), _step3; !(_iteratorNormalCompletion3 = (_step3 = _iterator3.next()).done); _iteratorNormalCompletion3 = true) {
          var _ret = _loop();

          if (_ret === 'break') break;
        }
      } catch (err) {
        _didIteratorError3 = true;
        _iteratorError3 = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion3 && _iterator3['return']) {
            _iterator3['return']();
          }
        } finally {
          if (_didIteratorError3) {
            throw _iteratorError3;
          }
        }
      }
    } else {
      require('fbjs/lib/warning')(false, 'setRelayModernMutationConfigs: RANGE_DELETE ' + 'pathToConnection is incorrect. Unable to find connection with ' + 'parentID: %s and path: %s', parentID, pathToConnection.toString());
    }
  } else {
    require('fbjs/lib/warning')(false, 'setRelayModernMutationConfigs: RANGE_DELETE ' + 'pathToConnection must include at least parent and connection');
  }
}

function getRootField(operation) {
  var rootField = void 0;
  if (operation.fragment && operation.fragment.selections && operation.fragment.selections.length > 0 && operation.fragment.selections[0].kind === 'LinkedField') {
    rootField = operation.fragment.selections[0].name;
  }
  return rootField;
}

module.exports = setRelayModernMutationConfigs;


//////////////////
// WEBPACK FOOTER
// ./~/react-relay/~/relay-runtime/lib/setRelayModernMutationConfigs.js
// module id = 645
// module chunks = 3